// they are being used idk they aren't beinf detected by eslint
import React, { useContext, useEffect, useMemo, useState } from "react";
import { WorkspaceStatus } from "models/Workspace.model";
import { OrgContext } from "contexts/OrgContext";
import UsernameDisplayField, {
  EmailDisplayField,
  UsernameDisplayCircle,
} from "components/UsernameDisplayField";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import FlatCard from "components/UI-lib/FlatCard";
import Spinner from "components/UI-lib/Spinner";
import { toast } from "utils/toast";
import { UserContext } from "contexts/UserContext";
import { Environment } from "components/Environment/utils/types";
import { getEnvironmentStatus } from "components/Environment/utils/environtmentUtils";
import { OrgRoleIds } from "models/Role.model";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "components/Graphs/Table";
import { Button as GraphButton } from "components/Graphs/Button";
import { Badge } from "components/Graphs/Badge";
import { Input } from "components/Graphs/Input";
import { createConnectClient } from "server/connect";
import { EnvironmentService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/environment_connect";
import { AddSSHAccessToEnvironmentRequest } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/environment_pb";
import { useEnvironmentNetworkAccess } from "hooks/environment/useEnvironmentNetworkAccess";
import { getSSHPortId } from "./addSSHAccessToEnvironmentRequest";
import {
  organizationAccessClient,
  type OrganizationMemberAccess,
} from "server/devplane/OrganizationAccesses";
import { organizationAccessLevelToOrgRole } from "utils/organizationAccess";

const environmentServiceClient = createConnectClient(EnvironmentService);

interface SSHAccessProps {
  environment: Environment;
  refetchEnvironment?: () => Promise<unknown>;
  unstyled?: boolean;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Unknown error occurred";
}

const SSHAccess: React.FC<SSHAccessProps> = (props: SSHAccessProps) => {
  const [loadingUsers, setLoadingUsers] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [userData, setUserData] = useState<Map<string, any>>(new Map());
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMemberAccess[]
  >([]);
  const [isLoadingOrganizationMembers, setIsLoadingOrganizationMembers] =
    useState(true);
  const orgContext = useContext(OrgContext);
  const workspaceContext = useContext(WorkspaceContext);
  const userContext = useContext(UserContext);
  const isSkybridgeEnabled =
    props.environment.labels?.["tunnel-provider"] === "skybridge";
  const { tcpPorts, isLoadingPorts } = useEnvironmentNetworkAccess(
    isSkybridgeEnabled ? props.environment : null,
  );
  const sshPortId = useMemo(() => getSSHPortId(tcpPorts), [tcpPorts]);
  const isWaitingForSkybridgeSSHPort = isSkybridgeEnabled && !sshPortId;

  const otherUsers = useMemo(() => {
    // Check if current user is an org owner
    const myMember = organizationMembers.find(
      (member) => member.userId === userContext.me?.id,
    );

    const amIOrgOwner =
      myMember &&
      organizationAccessLevelToOrgRole(myMember.accessLevel) ===
        OrgRoleIds.Owner;
    return organizationMembers
      .filter((member) => member.userId !== props.environment.labels?.userId)
      .filter((member) => {
        if (amIOrgOwner) {
          return true;
        } else {
          return member.userId !== userContext.me?.id;
        }
      });
  }, [organizationMembers, props.environment.labels?.userId, userContext.me]);

  // Load user data for all users
  useEffect(() => {
    const loadUserData = async () => {
      const newUserData = new Map();
      for (const member of otherUsers) {
        const user = await userContext.getUser(member.userId);
        if (user) {
          newUserData.set(member.userId, user);
        }
      }
      setUserData(newUserData);
    };

    if (otherUsers.length > 0) {
      loadUserData();
    }
  }, [otherUsers, userContext]);

  // Filter and sort users based on search term
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = otherUsers;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = otherUsers.filter((member) => {
        const user = userData.get(member.userId);
        const username = user?.username?.toLowerCase() || "";
        const email = user?.email?.toLowerCase() || "";
        const userId = member.userId.toLowerCase();

        return (
          username.includes(searchLower) ||
          email.includes(searchLower) ||
          userId.includes(searchLower)
        );
      });
    }

    // Sort by username
    return filtered.sort((a, b) => {
      const userA = userData.get(a.userId);
      const userB = userData.get(b.userId);
      const usernameA = userA?.username || a.userId;
      const usernameB = userB?.username || b.userId;

      return usernameA.localeCompare(usernameB);
    });
  }, [otherUsers, searchTerm, userData]);

  useEffect(() => {
    if (!orgContext.activeOrgId) {
      setOrganizationMembers([]);
      setIsLoadingOrganizationMembers(false);
      return;
    }

    let isCurrentOrganization = true;
    const loadMembers = async () => {
      setIsLoadingOrganizationMembers(true);

      try {
        const res = await organizationAccessClient.listMembers(
          orgContext.activeOrgId!,
        );
        if (isCurrentOrganization) {
          setOrganizationMembers(res.members);
        }
      } catch (error) {
        console.error("Failed to load organization members", error);
        if (isCurrentOrganization) {
          setOrganizationMembers([]);
        }
      } finally {
        if (isCurrentOrganization) {
          setIsLoadingOrganizationMembers(false);
        }
      }
    };

    void loadMembers();

    return () => {
      isCurrentOrganization = false;
    };
  }, [orgContext.activeOrgId]);

  const getShareSuccessMessage = (subjectId: string) => {
    const user = userData.get(subjectId);
    const recipient = user?.email || user?.username || subjectId;
    return `Shared SSH access with ${recipient}`;
  };

  const handleUserShare = async (subjectId: string) => {
    setLoadingUsers((prev) => new Set([...prev, subjectId]));

    try {
      if (isSkybridgeEnabled) {
        if (!props.environment.environment_id) {
          throw new Error("Missing environment ID");
        }

        if (!sshPortId) {
          throw new Error(
            isLoadingPorts
              ? "SSH port is still loading. Try again shortly."
              : "This environment does not have an SSH port to share.",
          );
        }

        await environmentServiceClient.addSSHAccessToEnvironment(
          new AddSSHAccessToEnvironmentRequest({
            environmentId: props.environment.environment_id,
            userId: subjectId,
            portId: sshPortId,
          }),
        );
        await props.refetchEnvironment?.();

        toast.success(getShareSuccessMessage(subjectId));
        return;
      }

      const res = await workspaceContext.shareWorkspace(
        props.environment.workspaceId || "",
        [subjectId],
        orgContext.activeOrgId,
      );

      if (res.success) {
        toast.success(getShareSuccessMessage(subjectId));
      } else {
        toast.error("Failed to add User to the Instance", {
          description: res.message,
        });
      }
    } catch (error) {
      toast.error("Failed to add User to the Instance", {
        description: getErrorMessage(error),
      });
    } finally {
      setLoadingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(subjectId);
        return newSet;
      });
    }
  };

  return (
    <>
      <div className="flex w-full flex-col items-stretch justify-start">
        <FlatCard
          className={
            props.unstyled
              ? "mb-3 w-full !rounded-none !bg-transparent !p-0"
              : "mb-3 w-full bg-secondaryBg"
          }
          noBorder={props.unstyled}
        >
          {props.environment &&
          getEnvironmentStatus(props.environment) ===
            WorkspaceStatus.Running ? (
            <>
              <div className="mb-4">
                <p className="text-sm text-secondary">
                  Manage SSH access for your organization members
                  <br />
                  <div className="flex flex-row items-center">
                    <div className="flex flex-row text-xs text-secondary p-1 bg-zinc-800 rounded-md mt-4">
                      <InformationCircleIcon className="h-4 w-4 mr-1" />
                      <p>
                        This is a beta feature and users CANNOT be removed once
                        they are added
                      </p>
                    </div>
                  </div>
                </p>
              </div>
              {isWaitingForSkybridgeSSHPort ? (
                <>
                  {isLoadingPorts ? (
                    <div className="flex items-center">
                      <Spinner
                        type="secondary"
                        className="h-4 w-4 text-gray-500 dark:text-secondary"
                      />
                      <span>Loading ports...</span>
                    </div>
                  ) : (
                    <span>
                      Waiting for SSH port to be ready before SSH access can be
                      shared
                    </span>
                  )}
                </>
              ) : (
                otherUsers.length > 0 && (
                  <div className="mt-6">
                    <Input
                      type="search"
                      placeholder="Search by username, email, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="max-w-full"
                    />
                  </div>
                )
              )}
              {!isWaitingForSkybridgeSSHPort &&
                (isLoadingOrganizationMembers ? (
                  <div
                    className="mt-5 flex items-center justify-center gap-2 py-4 text-sm text-secondary"
                    role="status"
                  >
                    <Spinner type="secondary" className="h-4 w-4" />
                    Loading organization members...
                  </div>
                ) : otherUsers.length > 0 ? (
                  <div className="mt-2">
                    <TableRoot>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>User</TableHeaderCell>
                            <TableHeaderCell>Manage</TableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAndSortedUsers.length > 0 ? (
                            filteredAndSortedUsers.map((member) => (
                              <TableRow
                                key={member.userId}
                                data-attribute={member.userId}
                              >
                                <TableCell>
                                  <div className="flex flex-row items-center">
                                    <UsernameDisplayCircle
                                      userId={member.userId}
                                    />
                                    <UsernameDisplayField
                                      userId={member.userId}
                                      className="text-gray-900 dark:text-white ml-1 font-medium"
                                    />
                                    <div className="text-gray-900 dark:text-white">
                                      &nbsp;•&nbsp;
                                    </div>
                                    <EmailDisplayField
                                      userId={member.userId}
                                      className="text-gray-900 dark:text-white text-xs"
                                    />
                                    <div className="text-gray-900 dark:text-white">
                                      &nbsp;•&nbsp;
                                    </div>
                                    <div className="text-gray-900 dark:text-white text-xs">
                                      {member.userId}
                                    </div>
                                  </div>
                                </TableCell>

                                <TableCell>
                                  <div className="flex flex-row items-center justify-start">
                                    {props.environment.ssh_access?.some(
                                      (access) =>
                                        access.user_id === member.userId,
                                    ) ? (
                                      <Badge variant="neutral">
                                        Already added
                                      </Badge>
                                    ) : (
                                      <GraphButton
                                        onClick={() =>
                                          handleUserShare(member.userId)
                                        }
                                        isLoading={loadingUsers.has(
                                          member.userId,
                                        )}
                                        disabled={loadingUsers.size > 0}
                                        variant="primary"
                                      >
                                        Add User
                                      </GraphButton>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <div className="text-center py-4">
                                  <span className="text-sm text-gray-700 dark:text-secondary">
                                    No users found matching "{searchTerm}"
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableRoot>
                  </div>
                ) : (
                  <div className="mt-5 text-center py-4">
                    <span className="text-sm text-gray-700 dark:text-secondary">
                      No one else is in the organization to share
                    </span>
                  </div>
                ))}
            </>
          ) : (
            <>Workspace needs to be running in order to share SSH access</>
          )}
        </FlatCard>
        <div />
      </div>
    </>
  );
};

export default SSHAccess;
